import { Fragment, useState } from 'react';
import type { EconCalcFact, EconExtractedRow, EconSource } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { labelMetric } from '@entities/economic/types';
import { TraceView } from '../TraceView';
import {
  FactRowButton,
  Muted,
  SectionLead,
  SectionTitle,
  Table,
  TableScroll,
  TraceBody,
  TraceCell,
  TraceToggle,
} from './styles';
import { fmtMetricValue } from './helpers';
import { ExtractedRows } from './ExtractedRows';

/** Трассировка одного факта (свёрнутая по умолчанию). Показывает цепочку
 *  факт → строки → документы. */
export function FactTraceRow({ fact }: { fact: EconCalcFact }) {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr>
        <td>{labelMetric(fact.metric)}</td>
        <td className="num">{fmtMetricValue(fact.metric, fact.value_fact)}</td>
        <td>
          <FactRowButton
            type="button"
            $open={open}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Скрыть цепочку' : 'Показать цепочку'}
          </FactRowButton>
        </td>
      </tr>
      {open && (
        <tr>
          <TraceCell colSpan={3}>
            <TraceView recordUid={fact.record_uid} fact={fact} />
          </TraceCell>
        </tr>
      )}
    </Fragment>
  );
}

/** Свёрнутый по умолчанию блок «Источники (трассировка)» — вспомогательная
 *  data-lineage панель: извлечённые строки + сверка показателей с источниками.
 *  Это НЕ основной результат, поэтому скрыт за раскрывающимся заголовком. */
export function SourcesSection({
  rows,
  facts,
  sources,
  runId,
  canWrite,
}: {
  rows: EconExtractedRow[];
  facts: EconCalcFact[];
  sources: EconSource[];
  runId: string;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <TraceToggle
        type="button"
        $open={open}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
        Источники (трассировка)
        <Badge $tone="neutral">
          строк: {rows.length} · показателей: {facts.length}
        </Badge>
      </TraceToggle>

      {open && (
        <TraceBody>
          <SectionLead>
            Полная трассировка: каждый показатель раскладывается до исходных строк и документов, из
            которых он посчитан. Строки можно поправить вручную — показатели пересчитаются.
          </SectionLead>

          <SectionTitle>Сверка показателей с источниками</SectionTitle>
          {facts.length === 0 ? (
            <Muted>Показатели появятся после завершения обработки.</Muted>
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Показатель</th>
                    <th style={{ textAlign: 'right' }}>Значение</th>
                    <th>Цепочка</th>
                  </tr>
                </thead>
                <tbody>
                  {facts.map((f) => (
                    <FactTraceRow key={f.id} fact={f} />
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}

          <SectionTitle style={{ marginTop: 24 }}>Извлечённые строки</SectionTitle>
          <ExtractedRows rows={rows} sources={sources} runId={runId} canWrite={canWrite} />
        </TraceBody>
      )}
    </Card>
  );
}
